import {
  Box,
  Typography,
  Switch,
  TextField,
  InputAdornment,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useContactCenterStyles } from "@components/virtual-receptionist/theming/useContactCenterStyles";
import Lottie from "react-lottie";
import { useEffect, useMemo, useState } from "react";
import { lottieOptions } from "../../../jobs2/jobs-calendar-widget/components/TodayJobsPlaceholder";
import { useSettingsMutation } from "../hooks/useSettingsMutation";
import { useAppSelector } from "../../../../redux/hooks/redux";
import { selectCurrentCompany } from "../../../../redux/features/user/selectors/userState.selectors";
import { CallFlowConfig } from "../types/update-company-config";
import { useRecordedCallsQuery } from "../../calls/recording/hooks/useRecordedCallsQuery";
import RecordingItem from "./RecordingItem";
import { useVirtualAssistant } from "@components/virtual-receptionist/context/VirtualAssistantContext";
import { useBulkResolveContacts } from "@components/virtual-receptionist/contacts/hooks/useBulkResolveContacts";
import {
  getDisplayName,
  getRoleLabel,
} from "@components/virtual-receptionist/contacts/types/bulk-resolver.types";

type Props = {
  settings: CallFlowConfig;
};

export default function RecordingSettings({ settings }: Props) {
  const styles = useContactCenterStyles();
  const { id: companyId } = useAppSelector(selectCurrentCompany);
  const { update } = useSettingsMutation();
  const { sessionNumber } = useVirtualAssistant();

  const [searchQuery, setSearchQuery] = useState("");
  const [recordCallsEnabled, setRecordCallsEnabled] = useState(
    settings.recordCallsEnabled
  );

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useRecordedCallsQuery({
      limit: 10,
      enabled: true,
      phoneNumber: sessionNumber,
    });

  useEffect(() => {
    setRecordCallsEnabled(settings.recordCallsEnabled);
  }, [settings.recordCallsEnabled]);

  const recordings = useMemo(() => {
    return data?.pages.flatMap((page) => page.data) ?? [];
  }, [data]);

  const unresolvedPhones = useMemo(() => {
    const phones = recordings
      .filter((rec) => !rec.contact)
      .map((rec) => rec.from || rec.to)
      .filter(Boolean);

    return Array.from(new Set(phones));
  }, [recordings]);

  const { data: bulkResolvedMap = {} } =
    useBulkResolveContacts(unresolvedPhones);

  const filteredRecordings = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return recordings;

    return recordings.filter((recording) => {
      const phoneNumber = recording.from || recording.to;
      const resolved =
        recording.contact || bulkResolvedMap?.[phoneNumber] || null;

      const from = recording.from?.toLowerCase() ?? "";
      const to = recording.to?.toLowerCase() ?? "";
      const callSid = recording.callSid?.toLowerCase() ?? "";
      const createdAt = recording.createdAt
        ? new Date(recording.createdAt).toLocaleDateString().toLowerCase()
        : "";

      const displayName = resolved
        ? getDisplayName(resolved)?.toLowerCase() ?? ""
        : "";

      const roleLabel = resolved
        ? getRoleLabel(resolved).toLowerCase()
        : "";

      return (
        from.includes(query) ||
        to.includes(query) ||
        callSid.includes(query) ||
        createdAt.includes(query) ||
        displayName.includes(query) ||
        roleLabel.includes(query)
      );
    });
  }, [recordings, searchQuery, bulkResolvedMap]);

  const handleToggleRecording = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = event.target.checked;
    setRecordCallsEnabled(checked);

    try {
      await update.mutateAsync({
        companyId,
        payload: { recordCallsEnabled: checked },
      });
    } catch (error) {
      setRecordCallsEnabled(!checked);
      console.error(error);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, p: 2 }}>
      <Box sx={styles.topCard}>
        <Box>
          <Typography
            sx={{ fontWeight: 800, fontSize: "1rem", color: "var(--fx-surface-text, #0F172A)" }}
          >
            Recorded calls
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontWeight: 500,
              display: "block",
              lineHeight: 1.1,
              mt: 0.8,
            }}
          >
            Automatically archive incoming and outgoing calls audio
          </Typography>
        </Box>

        <Switch
          checked={recordCallsEnabled}
          onChange={handleToggleRecording}
          disabled={update.isPending}
          sx={styles.accentSwitch}
        />
      </Box>

      <TextField
        fullWidth
        placeholder="Search recordings..."
        variant="outlined"
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={styles.searchField}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: "var(--fx-surface-texttertiary, #94A3B8)" }} />
            </InputAdornment>
          ),
        }}
      />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={1}
      >
        <Typography
          sx={{ fontWeight: 800, color: "var(--fx-surface-textsecondary, #64748B)", fontSize: ".95rem" }}
        >
          Recent Calls
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontWeight: 600 }}
        >
          {filteredRecordings.length} Result
          {filteredRecordings.length !== 1 ? "s" : ""}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {isLoading ? (
          <Box
            sx={{
              ...styles.topCard,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.85rem",
                color: "text.secondary",
                fontWeight: 500,
              }}
            >
              Loading recordings...
            </Typography>
          </Box>
        ) : filteredRecordings.length > 0 ? (
          <>
            {filteredRecordings.map((rec) => (
              <RecordingItem
                key={rec.uid}
                rec={rec}
                bulkResolvedMap={bulkResolvedMap}
              />
            ))}

            {hasNextPage && (
              <Button
                variant="outlined"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                sx={{
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 700,
                }}
              >
                {isFetchingNextPage ? "Loading more..." : "Load more"}
              </Button>
            )}
          </>
        ) : (
          <Box
            sx={{
              ...styles.topCard,
              minHeight: 220,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Lottie
              options={lottieOptions}
              height={50}
              width={50}
              isClickToPauseDisabled
            />
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "text.secondary",
                mt: 1,
                fontWeight: 500,
                lineHeight: 1.4,
              }}
            >
              {searchQuery
                ? `No recordings found for "${searchQuery}"`
                : "No recordings yet"}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
